/**
 * Holdings on a phone: the Data tab's card form (owner, 2026-10-14;
 * `docs/features/mobile.md`, "On a phone the views are cards you swipe").
 *
 * The same data and the same rules as `HoldingsView` — it reads
 * `lib/holdings-view.ts` and leaves that component, which the desktop and the
 * iPad use, untouched. What changes is the shape, so it fits 390px with nothing
 * scrolling sideways:
 *
 * - the summary is **three tiles**, not a four-column box;
 * - each holder is **two or three lines** — name and $ held, the bar with % of
 *   the company, then of book and the two quarters' changes — not one row with
 *   a column per quarter;
 * - *Quarter by quarter*, *Other holders* and *Options* **fold**.
 *
 * The rules kept: **missing is never selling** (an unfiled quarter is `·`), **$
 * is the trade, not the price move**, and the two percentages are labelled.
 */

import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";

import type { Holding, HoldingsSymbol } from "../api/holdings";
import { PageOverlay, type HoldingsPage } from "./HoldingsView";
import {
  dueDate, earlierHolders, fundColor, optionsHeavy, pct, quarterCell, quarterLabel, shortDate, summary, useHoldings, usd,
  type QuarterCell,
} from "../lib/holdings-view";

export function HoldingsCard({ symbol }: { symbol: string }): ReactElement {
  const load = useHoldings(symbol);
  const [page, setPage] = useState<HoldingsPage | null>(null);
  const setFund = (fund: string): void => setPage({ fund });
  if (load.state === "loading") return <p style={NOTE}>loading…</p>;
  if (load.state === "none") return <p style={NOTE}>Holdings are not available here yet.</p>;
  const h = load.data;
  return (
    <div style={WRAP} data-testid="holdings-card">
      <div style={ASOF}>
        Top 15 funds · {quarterLabel(h.window.base)} filed · {quarterLabel(h.window.qn)} due{" "}
        {shortDate(dueDate(h.window.qn))}
      </div>
      {h.state === "never" ? (
        <p style={NOTE}>None of the 15 funds holds {h.symbol}.</p>
      ) : (
        <>
          <Tiles h={h} />
          {h.holders.map((x) => (
            <HolderRow key={x.fund} x={x} h={h} onFund={setFund} />
          ))}
          {h.exited.map((x) => (
            <div key={x.fund} style={{ ...ROW, opacity: 0.6 }}>
              <div style={LINE}>
                <Name x={x} onFund={setFund} hollow />
                <span style={MUTED}>sold out in {quarterLabel(x.period)}</span>
              </div>
            </div>
          ))}
          {earlierHolders(h).map((fund) => (
            <div key={fund} style={{ ...ROW, opacity: 0.6 }}>
              <div style={LINE}>
                <span style={{ fontWeight: 600 }}>{h.funds[fund]?.name ?? fund}</span>
                <span style={MUTED}>held earlier in the year</span>
              </div>
            </div>
          ))}
          <Fold title="Quarter by quarter">
            <Quarters h={h} />
          </Fold>
        </>
      )}
      {h.others && (h.others.holders > 0 || h.others.exits.length > 0) && (
        <Fold title={`Other holders (${h.others.holders.toLocaleString()} filers)`}>
          <Others h={h} onFund={setFund} />
        </Fold>
      )}
      <OptionsFold h={h} onFund={setFund} />
      {page && <PageOverlay page={page} onPage={setPage} onClose={() => setPage(null)} />}
    </div>
  );
}

function Tiles({ h }: { h: HoldingsSymbol }): ReactElement {
  const s = summary(h);
  const base = quarterLabel(h.window.base);
  return (
    <div style={TILES}>
      <Tile k="$ held" v={usd(s.heldValue)} sub={<><Signed v={s.baseNet} /> {base}</>} />
      <Tile
        k="funds"
        v={`${s.holders} of 15`}
        sub={
          <>
            <span style={POS}>+{s.baseIn}</span> <span style={NEG}>−{s.baseOut}</span>
          </>
        }
      />
      <Tile k={`% of ${h.symbol}`} v={pct(s.sharesOutPct)} />
    </div>
  );
}

function Tile({ k, v, sub }: { k: string; v: string; sub?: ReactNode }): ReactElement {
  return (
    <div style={TILE}>
      <div style={{ ...MUTED, fontSize: 11 }}>{k}</div>
      <div style={{ fontWeight: 700, fontSize: 16 }}>{v}</div>
      {sub && <div style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

function HolderRow({ x, h, onFund }: { x: Holding; h: HoldingsSymbol; onFund: (fund: string) => void }): ReactElement {
  const max = Math.max(1, ...h.holders.map((y) => y.value));
  const w = Math.max(1.5, (100 * x.value) / max);
  return (
    <div style={ROW}>
      <div style={LINE}>
        <Name x={x} onFund={onFund} />
        <span style={{ fontWeight: 600 }}>{usd(x.value)}</span>
      </div>
      <div style={LINE}>
        <span style={TRACK}>
          <span style={{ ...FILL, width: `${w}%`, background: statusColor(x.status) }} />
        </span>
        <span style={{ ...MUTED, whiteSpace: "nowrap" }}>
          {x.pctOut !== null ? `${pct(x.pctOut)} of ${h.symbol}` : ""}
        </span>
      </div>
      <div style={{ ...LINE, justifyContent: "flex-start", gap: 6, fontSize: 12 }}>
        <span style={MUTED}>
          of book {pct(x.pctBook)}
          {optionsHeavy(x.stockShare) ? " ⚠" : ""}
        </span>
        <span style={MUTED}>·</span>
        <QuarterChange label={quarterLabel(h.window.base)} c={quarterCell(h, x.fund, h.window.base)} />
        <span style={MUTED}>·</span>
        <QuarterChange label={quarterLabel(h.window.qn)} c={quarterCell(h, x.fund, h.window.qn)} />
      </div>
    </div>
  );
}

/** One quarter's change for one holder. Unfiled is `·`, never $0. */
function QuarterChange({ label, c }: { label: string; c: QuarterCell }): ReactElement {
  const body =
    c.kind === "pending" ? (
      <span style={MUTED} title="not filed yet">·</span>
    ) : c.kind === "move" ? (
      <span style={c.trade >= 0 ? POS : NEG}>
        {usd(c.trade, true)}
        {c.status === "NEW" || c.status === "EXIT" ? ` ${c.status}` : ""}
      </span>
    ) : c.kind === "held" ? (
      <span style={MUTED}>—</span>
    ) : (
      <span style={MUTED}> </span>
    );
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <span style={MUTED}>{label} </span>
      {body}
    </span>
  );
}

function Quarters({ h }: { h: HoldingsSymbol }): ReactElement {
  return (
    <>
      {[...h.ledger].reverse().map((q) => (
        <div key={q.period} style={{ ...ROW, gap: 2 }}>
          <div style={LINE}>
            <span style={{ fontWeight: 600 }}>
              {q.label}
              {q.pending ? <span style={MUTED}> · filing</span> : null}
            </span>
            <span>
              <span style={POS}>{usd(q.bought)}</span> <span style={MUTED}>/</span>{" "}
              <span style={NEG}>{usd(-q.sold, true)}</span>
            </span>
          </div>
          {[...q.adds, ...q.cuts].map((m) => (
            <div key={m.fund} style={{ ...LINE, fontSize: 12 }}>
              <span style={MUTED}>{m.fundName}</span>
              <span style={m.trade >= 0 ? POS : NEG}>
                {usd(m.trade, true)}
                {m.status === "NEW" || m.status === "EXIT" ? ` ${m.status}` : ""}
              </span>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function Others({ h, onFund }: { h: HoldingsSymbol; onFund: (fund: string) => void }): ReactElement | null {
  const o = h.others;
  if (!o) return null;
  return (
    <>
      <div style={LINE}>
        <span>Top 15</span>
        <span>
          {usd(o.top15Value)} · {pct(o.top15PctOut)}
        </span>
      </div>
      <div style={LINE}>
        <span>Others ({o.holders.toLocaleString()})</span>
        <span>
          {usd(o.value)} · {pct(o.pctOut)}
        </span>
      </div>
      {o.rows.map((x) => (
        <div key={x.fund} style={{ ...LINE, fontSize: 13 }}>
          <Name x={x} onFund={onFund} hollow />
          <span>{usd(x.value)}</span>
        </div>
      ))}
      {o.exits.length > 0 && <div style={{ ...MUTED, fontSize: 12, marginTop: 4 }}>Largest exits</div>}
      {o.exits.map((x) => (
        <div key={x.fund} style={{ ...LINE, fontSize: 13, opacity: 0.7 }}>
          <Name x={x} onFund={onFund} hollow />
          <span style={NEG}>{usd(x.trade, true)}</span>
        </div>
      ))}
    </>
  );
}

function OptionsFold({ h, onFund }: { h: HoldingsSymbol; onFund: (fund: string) => void }): ReactElement | null {
  const rows = [...h.holders, ...h.optionsOnly].filter(
    (x, i, a) => (x.calls > 0 || x.puts > 0) && a.findIndex((y) => y.fund === x.fund) === i,
  );
  if (!rows.length) return null;
  return (
    <Fold title="Options (calls · puts, never in $ held)">
      {rows.map((x) => (
        <div key={x.fund} style={{ ...LINE, fontSize: 13 }}>
          <Name x={x} onFund={onFund} />
          <span>
            <span style={POS}>{x.calls ? usd(x.calls) : "·"}</span> <span style={MUTED}>·</span>{" "}
            <span style={NEG}>{x.puts ? usd(x.puts) : "·"}</span>
          </span>
        </div>
      ))}
    </Fold>
  );
}

function Fold({ title, children }: { title: string; children: ReactNode }): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} style={FOLD}>
        <span style={{ width: 14 }}>{open ? "▾" : "▸"}</span>
        {title}
      </button>
      {open && <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 0 4px 14px" }}>{children}</div>}
    </div>
  );
}

function Name({ x, onFund, hollow }: { x: Holding; onFund: (fund: string) => void; hollow?: boolean }): ReactElement {
  return (
    <button type="button" onClick={() => onFund(x.fund)} style={NAME} title={`${x.fundName} · ${x.manager}`}>
      <i
        style={{
          ...DOT,
          ...(hollow ? { border: "1px solid var(--qf-text-muted)" } : { background: fundColor(x.fundOrder) }),
        }}
      />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{x.fundName}</span>
    </button>
  );
}

function Signed({ v }: { v: number }): ReactElement {
  return <span style={v >= 0 ? POS : NEG}>{usd(v, true)}</span>;
}

function statusColor(s: Holding["status"]): string {
  return s === "TRIM" || s === "EXIT" ? "var(--qf-negative)" : s === "NEW" || s === "ADD" ? "var(--qf-positive)" : "var(--qf-accent)";
}

const WRAP: CSSProperties = { padding: 12, display: "flex", flexDirection: "column", gap: 10, color: "var(--qf-text)", fontSize: 14 };
const NOTE: CSSProperties = { padding: 16, color: "var(--qf-text-secondary)", fontSize: 13 };
const ASOF: CSSProperties = { fontSize: 12, color: "var(--qf-text-muted)" };
const TILES: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 };
const TILE: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 8, padding: "6px 8px", minWidth: 0 };
const ROW: CSSProperties = { display: "flex", flexDirection: "column", gap: 3, paddingBottom: 8, borderBottom: "1px solid var(--qf-border)" };
const LINE: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minWidth: 0 };
const TRACK: CSSProperties = { flex: 1, height: 8, borderRadius: 4, background: "var(--qf-surface-alt, var(--qf-border))", overflow: "hidden", position: "relative" };
const FILL: CSSProperties = { position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 4 };
const NAME: CSSProperties = { display: "flex", alignItems: "center", gap: 6, minWidth: 0, padding: 0, border: 0, background: "none", font: "inherit", fontWeight: 600, color: "var(--qf-text)", textAlign: "left", cursor: "pointer", whiteSpace: "nowrap" };
const DOT: CSSProperties = { width: 8, height: 8, borderRadius: 999, flex: "0 0 auto", display: "inline-block" };
const FOLD: CSSProperties = { display: "flex", alignItems: "center", gap: 4, width: "100%", minHeight: 40, padding: 0, border: 0, background: "none", font: "inherit", fontWeight: 600, color: "var(--qf-text)", textAlign: "left", cursor: "pointer" };
const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
const POS: CSSProperties = { color: "var(--qf-positive)" };
const NEG: CSSProperties = { color: "var(--qf-negative)" };
